// Added by Sejune Oh on 2020-04-06
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { FC, useState } from 'react'
import { createStyles, Theme, makeStyles, useTheme, InputBaseComponentProps } from '@material-ui/core'

import MomentUtils from '@date-io/moment'
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers'
import { FormControl } from '@/components/_primes/v2'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
export type ParsableDate = object | string | number | Date | null | undefined
export interface DatePickerProps {
  /** Element's ID */
  id: string
  /** Date format (Ex : MM/dd/yyyy) */
  format?: string
  /** Label */
  label: string
  /** Variatn */
  variant?: 'dialog' | 'inline' | 'static'
  /** Margin */
  margin?: 'none' | 'dense' | 'normal'
  /** Min date */
  minDate?: Date
  /** Disabled */
  disabled?: boolean
  /** Selected Date */
  selectedDate: ParsableDate
  /** Date change evt handler */
  onDateChange: (date: any, value?: string | null | undefined) => void
  /** Input props */
  inputProps?: InputBaseComponentProps
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Date picker UI
 * @param id
 * @param label
 * @param margin: none | dense | normal
 * @param variant: dialog | inline | static
 * @param format : Date format (Ex : MM/dd/yyyy)
 * @param disabled
 * @param selectedDate
 * @param onDateChange
 * @param inputProps
 */
const DatePicker: FC<DatePickerProps> = ({
  id,
  label,
  variant = 'inline',
  margin = 'none',
  format = 'YYYY/MM/DD',
  minDate,
  disabled = false,
  selectedDate,
  onDateChange,
  inputProps,
  ...props
}) => {
  const classes = useStyles(useTheme())

  const [focused, setFocused] = useState(false)

  // # Blur evt handler
  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => setFocused(false)
  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => setFocused(true)

  return (
    <FormControl variant="outlined" className={classes.formControlWrapper}>
      {label && (
        <span className={classes.inputLabel} data-on-focused={focused} data-disabled={disabled}>
          {label}
        </span>
      )}
      <MuiPickersUtilsProvider utils={MomentUtils}>
        <KeyboardDatePicker
          id={id}
          variant={variant}
          format={format}
          margin={margin}
          minDate={minDate}
          value={selectedDate}
          onChange={onDateChange}
          disabled={disabled}
          className={classes.inputWrapper}
          data-on-focused={focused}
          data-disabled={disabled}
          InputProps={{
            className: classes.input,
          }}
          inputProps={{
            onBlur: handleBlur,
            onFocus: handleFocus,
            ...inputProps,
          }}
        />
      </MuiPickersUtilsProvider>
    </FormControl>
  )
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ! Styles
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formControlWrapper: {
      position: 'relative',
      width: '100%',
      display: 'flex',
    },
    inputLabel: {
      transition: 'color 200ms cubic-bezier(0, 0, 0.2, 1) 0ms',
      color: '#fff',
      display: 'block',
      flex: 1,
      marginBottom: '4px',
      '&[data-on-focused=true]': {
        color: '#145c82',
        fontWeight: 'bold'
      },
      '&[data-disabled=true]': {
        color: 'rgba(255, 255, 255, 0.23)',
      },
    },
    inputWrapper: {
      padding: '6px 0 5px 12px',
      position: 'relative',
      '&:before': {
        position: 'absolute',
        content: '""',
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        border: '1px solid rgba(255, 255, 255, 0.23)',
        borderRadius: '4px',
        transition:
          'border-color 200ms cubic-bezier(0, 0, 0.2, 1) 0ms, border-width 200ms cubic-bezier(0, 0, 0.2, 1) 0ms',
        pointerEvents: 'none',
      },
      '&:hover:before': {
        borderColor: '#fff',
      },
      '&[data-on-focused=true]:before': {
        borderWidth: '2px',
        borderColor: '#145c82',
      },
      '& > p' : {
        display: 'none'
      }
    },
    input: {
      zIndex: 1,
      '&:before': {
        content: 'none',
      },
      '&:after': {
        content: 'none',
      },
    },
  })
)

export default DatePicker
